/**
 * Toasts, ported from the live page's showToast().
 *
 * Same look, same 3-second dwell, same fade. The difference is that the old one
 * appended a div and set a timeout from anywhere in 3,731 lines of script; here
 * a toast is state, and the only way to raise one is through the context that
 * owns it.
 *
 * @module web/components/Toast
 */
import { useEffect, useState } from "react";

export type ToastTone = "" | "error" | "success";

export interface Notice {
  message: string;
  tone: ToastTone;
  /** Distinguishes two identical messages in a row, so the second still animates. */
  id: number;
}

const DWELL_MS = 3000;

export function Toast({ notice }: { notice: Notice | null }) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!notice) return;
    // Two frames, exactly as the original: the element has to be in the
    // document with the un-shown styles applied before the class flips, or the
    // browser has nothing to transition FROM and the toast simply appears.
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
    const timer = setTimeout(() => setShown(false), DWELL_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      setShown(false);
    };
  }, [notice]);

  return (
    <div className="toast-container">
      {notice && (
        <div className={`toast${notice.tone ? ` ${notice.tone}` : ""}${shown ? " show" : ""}`} role="status">
          {notice.message}
        </div>
      )}
    </div>
  );
}
